import React, { useEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { Link } from 'react-router-dom';
import type { Editor } from '@tiptap/core';
import {
  getCharacterCueDialogueDecorations,
  getSceneDialogueStats,
  getSceneRange,
  isSceneHeadingParagraphText,
  type SceneDialogueStats,
} from '@narrativebench/editor-core';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { useHeadingSectionMenu } from '../contexts/HeadingSectionMenuContext';
import {
  getFeedbackAnalyzeDisableTooltip,
  getSectionAnalyzeDisableReason,
} from '../utils/feedback-analyze-availability';
import { getTextLengthStats } from '../utils/word-count';
import { formatSceneDialogueStatLabels } from '../utils/script-scene-stats';
import { copyTextToClipboard } from '../utils/copy-text-to-clipboard';
import { NOTES_CONTENT_MAX_LENGTH } from '../utils/note-utils';
import {
  resolveHoveredParagraph,
  type HoveredParagraphRange,
} from '../utils/resolve-hovered-paragraph';
import {
  isPointerApproachingParagraphSectionMenu,
  positionParagraphSectionMenu,
  type ParagraphSectionMenuPosition,
} from '../utils/paragraph-section-menu-position';
import {
  findScrollContainer,
  getMountedEditorDom,
  preserveScrollDuring,
} from '../utils/editor-utils';
import { getDocumentPlainText } from '../utils/document-plain-text';
import {
  ensureDocumentSpeechLanguage,
  speakDocumentText,
} from '../utils/document-speech';
import { toBcp47Locale } from '../locales/locale';
import { trackFeatureUsage } from '../utils/track-feature-usage';
import { useBlockSectionScopeHighlight } from '../utils/block-section-scope-highlight';
import {
  BlockMenuAnalyzeItem,
  BlockMenuChatItem,
  BlockMenuCopyItem,
  BlockMenuMoveToNoteItem,
  BlockMenuReadAloudItem,
  BlockMenuScriptInfoSection,
} from './block-section-menu/BlockSectionMenuItems';
import { BlockSectionMenuShell } from './block-section-menu/BlockSectionMenuShell';
import {
  isPointerOverSceneHeadingScriptInfo,
  SCENE_HEADING_SCRIPT_INFO_ENTER_EVENT,
} from './SceneHeadingScriptInfo';

type MenuPosition = ParagraphSectionMenuPosition;

const HIDE_DELAY_MS = 220;

function positionForParagraph(editor: Editor, from: number): MenuPosition | null {
  try {
    const dom = editor.view.nodeDOM(from);
    if (!(dom instanceof HTMLElement)) {
      return null;
    }
    return positionParagraphSectionMenu(dom);
  } catch {
    return null;
  }
}

function isPointerApproachingTrigger(
  editor: Editor,
  range: HoveredParagraphRange,
  clientX: number,
  clientY: number,
): boolean {
  try {
    const dom = editor.view.nodeDOM(range.from);
    if (!(dom instanceof HTMLElement)) {
      return false;
    }
    return isPointerApproachingParagraphSectionMenu(dom, clientX, clientY);
  } catch {
    return false;
  }
}

function resolveParagraphActionRange(
  editor: Editor,
  paragraphFrom: number,
): { from: number; to: number; isSceneHeading: boolean } | null {
  const node = editor.state.doc.nodeAt(paragraphFrom);
  if (!node || node.type.name !== 'paragraph') {
    return null;
  }
  if (isSceneHeadingParagraphText(node.textContent)) {
    const sceneRange = getSceneRange(editor.state.doc, paragraphFrom);
    if (sceneRange) {
      return { ...sceneRange, isSceneHeading: true };
    }
  }
  return {
    from: paragraphFrom,
    to: paragraphFrom + node.nodeSize,
    isSceneHeading: false,
  };
}

export const ParagraphSectionMenu: React.FC<{ editor: Editor | null }> = ({ editor }) => {
  const { t, locale } = useLocale();
  const { token, masterKey } = useAuth();
  const { showToast, showErrorToast, hideToast } = useToast();
  const menu = useHeadingSectionMenu();
  const [range, setRange] = useState<HoveredParagraphRange | null>(null);
  const [scopeRange, setScopeRange] = useState<{ from: number; to: number } | null>(null);
  const [isSceneHeadingTarget, setIsSceneHeadingTarget] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveringChrome, setHoveringChrome] = useState(false);
  const [disableReason, setDisableReason] = useState<string | undefined>();
  const [sceneDialogueStats, setSceneDialogueStats] = useState<SceneDialogueStats | null>(null);
  const hideTimeoutRef = useRef<number | null>(null);
  const menuOpenRef = useRef(false);
  const hoveringChromeRef = useRef(false);
  const rangeRef = useRef<HoveredParagraphRange | null>(null);
  const suppressUntilMouseMoveRef = useRef(false);
  const floatingRef = useRef<HTMLDivElement | null>(null);
  const lastPointerRef = useRef({ x: 0, y: 0 });

  menuOpenRef.current = menuOpen;
  hoveringChromeRef.current = hoveringChrome;
  rangeRef.current = range;

  useBlockSectionScopeHighlight(
    editor,
    menuOpen || hoveringChrome ? scopeRange : null,
  );

  const clearHideTimeout = (): void => {
    if (hideTimeoutRef.current != null) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const forceHideMenu = (): void => {
    clearHideTimeout();
    setMenuOpen(false);
    setHoveringChrome(false);
    hoveringChromeRef.current = false;
    menuOpenRef.current = false;
    setRange(null);
    setScopeRange(null);
    setIsSceneHeadingTarget(false);
    setPosition(null);
    setDisableReason(undefined);
  };

  const isPointerOverMenuTarget = (clientX: number, clientY: number): boolean => {
    if (isPointerOverSceneHeadingScriptInfo(clientX, clientY)) {
      return false;
    }
    if (menuOpenRef.current || hoveringChromeRef.current) {
      return true;
    }
    const floating = floatingRef.current;
    if (floating) {
      const rect = floating.getBoundingClientRect();
      if (
        clientX >= rect.left
        && clientX <= rect.right
        && clientY >= rect.top
        && clientY <= rect.bottom
      ) {
        return true;
      }
    }
    if (
      editor
      && !editor.isDestroyed
      && rangeRef.current
      && isPointerApproachingTrigger(editor, rangeRef.current, clientX, clientY)
    ) {
      return true;
    }
    return false;
  };

  const hideMenu = (): void => {
    if (menuOpenRef.current || hoveringChromeRef.current) {
      return;
    }
    const { x, y } = lastPointerRef.current;
    if (isPointerOverMenuTarget(x, y)) {
      return;
    }
    setRange(null);
    setScopeRange(null);
    setIsSceneHeadingTarget(false);
    setPosition(null);
    setDisableReason(undefined);
  };

  const scheduleHide = (): void => {
    clearHideTimeout();
    hideTimeoutRef.current = window.setTimeout(() => {
      hideMenu();
    }, HIDE_DELAY_MS);
  };

  const showForRange = (nextRange: HoveredParagraphRange): void => {
    if (!editor || editor.isDestroyed || suppressUntilMouseMoveRef.current) {
      return;
    }
    clearHideTimeout();
    const nextPosition = positionForParagraph(editor, nextRange.from);
    if (!nextPosition) {
      return;
    }
    const actionRange = resolveParagraphActionRange(editor, nextRange.from);
    setRange((previous) => {
      if (previous?.from === nextRange.from && previous?.to === nextRange.to) {
        return previous;
      }
      return nextRange;
    });
    if (actionRange) {
      setScopeRange((previous) => {
        if (previous?.from === actionRange.from && previous?.to === actionRange.to) {
          return previous;
        }
        return { from: actionRange.from, to: actionRange.to };
      });
      setIsSceneHeadingTarget(actionRange.isSceneHeading);
    } else {
      setScopeRange(null);
      setIsSceneHeadingTarget(false);
    }
    setPosition((previous) => {
      if (
        previous &&
        Math.abs(previous.top - nextPosition.top) < 0.5 &&
        Math.abs(previous.left - nextPosition.left) < 0.5
      ) {
        return previous;
      }
      return nextPosition;
    });
  };

  useEffect(() => {
    if (!editor || !menu || !editor.isEditable) {
      setRange(null);
      setScopeRange(null);
      setIsSceneHeadingTarget(false);
      setPosition(null);
      return undefined;
    }

    let attachedDom: HTMLElement | null = null;

    const handlePointerMove = (event: MouseEvent): void => {
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      if (suppressUntilMouseMoveRef.current) {
        suppressUntilMouseMoveRef.current = false;
      }
      if (isPointerOverSceneHeadingScriptInfo(event.clientX, event.clientY, event.target)) {
        hoveringChromeRef.current = false;
        setHoveringChrome(false);
        if (!menuOpenRef.current) {
          forceHideMenu();
        }
        return;
      }
      if (menuOpenRef.current || hoveringChromeRef.current) {
        return;
      }
      const nextRange = resolveHoveredParagraph(
        editor.view,
        event.clientX,
        event.clientY,
        event.target,
      );
      if (!nextRange) {
        if (
          rangeRef.current
          && isPointerApproachingTrigger(
            editor,
            rangeRef.current,
            event.clientX,
            event.clientY,
          )
        ) {
          clearHideTimeout();
          return;
        }
        scheduleHide();
        return;
      }
      showForRange(nextRange);
    };

    const handlePointerLeave = (event: MouseEvent): void => {
      if (menuOpenRef.current || hoveringChromeRef.current) {
        return;
      }
      const related = event.relatedTarget;
      if (related instanceof Node && floatingRef.current?.contains(related)) {
        return;
      }
      if (isPointerOverMenuTarget(event.clientX, event.clientY)) {
        return;
      }
      scheduleHide();
    };

    const handleDocumentPointerMove = (event: MouseEvent): void => {
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      if (isPointerOverSceneHeadingScriptInfo(event.clientX, event.clientY, event.target)) {
        if (!menuOpenRef.current) {
          forceHideMenu();
        } else {
          hoveringChromeRef.current = false;
          setHoveringChrome(false);
        }
        return;
      }
      if (!rangeRef.current || menuOpenRef.current || hoveringChromeRef.current) {
        return;
      }
      if (isPointerOverMenuTarget(event.clientX, event.clientY)) {
        clearHideTimeout();
      }
    };

    const handleSceneHeadingScriptInfoEnter = (): void => {
      hoveringChromeRef.current = false;
      setHoveringChrome(false);
      if (!menuOpenRef.current) {
        forceHideMenu();
      }
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.isComposing) {
        return;
      }
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      const { key } = event;
      if (
        key === 'Shift'
        || key === 'Control'
        || key === 'Alt'
        || key === 'Meta'
        || key === 'CapsLock'
        || key === 'Escape'
        || key === 'Tab'
        || key === 'Dead'
      ) {
        return;
      }
      suppressUntilMouseMoveRef.current = true;
      forceHideMenu();
    };

    const updatePosition = (): void => {
      if (suppressUntilMouseMoveRef.current) {
        return;
      }
      const currentRange = rangeRef.current;
      if (!currentRange) {
        return;
      }
      const nextPosition = positionForParagraph(editor, currentRange.from);
      if (!nextPosition) {
        hideMenu();
        return;
      }
      setPosition(nextPosition);
    };

    const detach = (): void => {
      if (!attachedDom) {
        return;
      }
      attachedDom.removeEventListener('mousemove', handlePointerMove);
      attachedDom.removeEventListener('mouseleave', handlePointerLeave);
      attachedDom.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousemove', handleDocumentPointerMove);
      window.removeEventListener(SCENE_HEADING_SCRIPT_INFO_ENTER_EVENT, handleSceneHeadingScriptInfoEnter);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      attachedDom = null;
    };

    const attach = (): void => {
      const dom = getMountedEditorDom(editor);
      if (!dom || dom === attachedDom) {
        return;
      }
      detach();
      attachedDom = dom;
      dom.addEventListener('mousemove', handlePointerMove);
      dom.addEventListener('mouseleave', handlePointerLeave);
      dom.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousemove', handleDocumentPointerMove);
      window.addEventListener(SCENE_HEADING_SCRIPT_INFO_ENTER_EVENT, handleSceneHeadingScriptInfoEnter);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    };

    attach();
    editor.on('create', attach);

    return () => {
      clearHideTimeout();
      editor.off('create', attach);
      detach();
    };
  }, [editor, menu]);

  useEffect(() => {
    if (!menuOpen || !editor || editor.isDestroyed) {
      return undefined;
    }
    const editorDom = getMountedEditorDom(editor);
    if (!editorDom) {
      return undefined;
    }
    const scrollContainer = findScrollContainer(editorDom);
    if (!scrollContainer) {
      return undefined;
    }
    const scrollTop = scrollContainer.scrollTop;
    const scrollLeft = scrollContainer.scrollLeft;

    const onScroll = (): void => {
      if (scrollContainer.scrollTop !== scrollTop) {
        scrollContainer.scrollTop = scrollTop;
      }
      if (scrollContainer.scrollLeft !== scrollLeft) {
        scrollContainer.scrollLeft = scrollLeft;
      }
    };

    scrollContainer.addEventListener('scroll', onScroll);
    const restoreFrame = window.requestAnimationFrame(() => {
      onScroll();
      window.requestAnimationFrame(onScroll);
    });

    return () => {
      window.cancelAnimationFrame(restoreFrame);
      scrollContainer.removeEventListener('scroll', onScroll);
    };
  }, [menuOpen, editor]);

  const refreshParagraphAvailability = (): {
    from: number;
    to: number;
    wordCount: number;
    text: string;
  } | null => {
    const activeRange = rangeRef.current;
    if (!editor || !activeRange) {
      setDisableReason(undefined);
      setSceneDialogueStats(null);
      setScopeRange(null);
      setIsSceneHeadingTarget(false);
      return null;
    }
    const actionRange = resolveParagraphActionRange(editor, activeRange.from);
    if (!actionRange) {
      setDisableReason(undefined);
      setSceneDialogueStats(null);
      setScopeRange(null);
      setIsSceneHeadingTarget(false);
      return null;
    }
    const { from, to, isSceneHeading } = actionRange;
    const text = getDocumentPlainText(editor.state.doc, from, to);
    const { wordCount } = getTextLengthStats(text);
    const reason = getSectionAnalyzeDisableReason(wordCount);
    setDisableReason(getFeedbackAnalyzeDisableTooltip(reason, t));
    setScopeRange({ from, to });
    setIsSceneHeadingTarget(isSceneHeading);
    if (isSceneHeading) {
      setSceneDialogueStats(
        getSceneDialogueStats(
          editor.state.doc,
          from,
          getCharacterCueDialogueDecorations(editor.state),
        ),
      );
    } else {
      setSceneDialogueStats(null);
    }
    return { from, to, wordCount, text };
  };

  const handleAnalyze = (): void => {
    if (!menu || menu.isAnalyzing) {
      return;
    }
    const next = refreshParagraphAvailability();
    if (!next || getSectionAnalyzeDisableReason(next.wordCount) !== null) {
      return;
    }
    setMenuOpen(false);
    menu.onAnalyzeSection(next.from, next.to);
    hideMenu();
  };

  const handleChat = (): void => {
    if (!menu) {
      return;
    }
    const next = refreshParagraphAvailability();
    if (!next) {
      return;
    }
    setMenuOpen(false);
    menu.onChatAboutSection(next.from, next.to);
    hideMenu();
  };

  const handleCopy = (): void => {
    const next = refreshParagraphAvailability();
    if (!next) {
      return;
    }
    setMenuOpen(false);
    void copyTextToClipboard(next.text).then((copied) => {
      if (copied) {
        showToast(t('copied_to_clipboard'));
      }
    });
    hideMenu();
  };

  const dismissMenuWithoutScrolling = (afterDismiss?: () => void): void => {
    if (!editor || editor.isDestroyed) {
      flushSync(() => {
        forceHideMenu();
      });
      afterDismiss?.();
      return;
    }
    const activeEditor = editor;
    preserveScrollDuring(activeEditor, () => {
      flushSync(() => {
        forceHideMenu();
      });
      afterDismiss?.();
      if (!activeEditor.isDestroyed) {
        activeEditor.commands.focus(undefined, { scrollIntoView: false });
      }
    });
  };

  const handleReadAloud = (): void => {
    const next = refreshParagraphAvailability();
    if (!next || !next.text.trim()) {
      dismissMenuWithoutScrolling();
      return;
    }
    dismissMenuWithoutScrolling(() => {
      ensureDocumentSpeechLanguage(toBcp47Locale(locale));
      const started = speakDocumentText(next.text, {
        sourceRange: { from: next.from, to: next.to },
      });
      if (started) {
        void trackFeatureUsage({
          feature: 'document_read_aloud',
          documentId: menu?.documentId,
          token,
          masterKey,
        });
      }
    });
  };

  const handleMoveToNote = (): void => {
    if (!menu?.onMoveToNote || !menu.projectId || !editor) {
      return;
    }
    const next = refreshParagraphAvailability();
    if (!next) {
      return;
    }
    const content = next.text.replace(/<[^>]*>/g, '').trim();
    const truncated =
      content.length > NOTES_CONTENT_MAX_LENGTH
        ? content.slice(0, NOTES_CONTENT_MAX_LENGTH)
        : content;
    if (!truncated) {
      setMenuOpen(false);
      return;
    }
    setMenuOpen(false);
    if (editor.state.doc.childCount === 1) {
      editor.chain().deleteRange({ from: next.from + 1, to: next.to - 1 }).run();
    } else {
      editor.chain().deleteRange({ from: next.from, to: next.to }).run();
    }
    hideMenu();
    void (async () => {
      try {
        const result = await menu.onMoveToNote!(truncated);
        if (result) {
          showToast(
            <>
              {t('note_created_toast_suffix_before')}
              <Link
                to={`/projects/${result.projectId}/notes`}
                style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600 }}
                onClick={() => hideToast()}
              >
                {t('note_created_toast_link_text')}
              </Link>
              {t('note_created_toast_suffix_after')}
            </>,
          );
        }
      } catch {
        showErrorToast(t('failed_to_create_note') || 'Failed to create note');
      }
    })();
  };

  if (!editor || !menu || !editor.isEditable || !range || !position) {
    return null;
  }

  const canMoveToNote = Boolean(menu.projectId && menu.onMoveToNote);
  const scriptInfoLabels = sceneDialogueStats
    ? formatSceneDialogueStatLabels(sceneDialogueStats, t)
    : null;

  return createPortal(
    <div
      ref={floatingRef}
      className="paragraph-section-menu-floating"
      style={{ top: position.top, left: position.left }}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onMouseEnter={() => {
        hoveringChromeRef.current = true;
        setHoveringChrome(true);
        clearHideTimeout();
      }}
      onMouseLeave={(event) => {
        hoveringChromeRef.current = false;
        setHoveringChrome(false);
        lastPointerRef.current = { x: event.clientX, y: event.clientY };
        if (!menuOpenRef.current) {
          scheduleHide();
        }
      }}
    >
      <BlockSectionMenuShell
        menuOpen={menuOpen}
        onMenuOpenChange={(open) => {
          menuOpenRef.current = open;
          if (open) {
            hoveringChromeRef.current = true;
          }
          if (!editor) {
            setMenuOpen(open);
            if (open) {
              setHoveringChrome(true);
            }
            return;
          }
          preserveScrollDuring(editor, () => {
            setMenuOpen(open);
            if (open) {
              setHoveringChrome(true);
              refreshParagraphAvailability();
            } else if (!hoveringChromeRef.current) {
              scheduleHide();
            }
          });
        }}
        ariaLabel={t('paragraph_section_menu')}
        className="paragraph-section-menu-floating-shell"
      >
        <BlockMenuAnalyzeItem
          isAnalyzing={Boolean(menu.isAnalyzing)}
          disableReason={disableReason}
          onAnalyze={handleAnalyze}
        />
        <BlockMenuChatItem
          onChat={handleChat}
          labelKey={isSceneHeadingTarget ? 'chat_about_this_section' : 'chat_about_this_paragraph'}
        />
        <BlockMenuReadAloudItem onReadAloud={handleReadAloud} />
        <BlockMenuCopyItem onCopy={handleCopy} />
        {canMoveToNote ? <BlockMenuMoveToNoteItem onMoveToNote={handleMoveToNote} /> : null}
        {scriptInfoLabels ? <BlockMenuScriptInfoSection labels={scriptInfoLabels} /> : null}
      </BlockSectionMenuShell>
    </div>,
    document.body,
  );
};
